import { IndividualPlanCode, individualPlanSeats } from '../../../lib/Paddle';

// Higher rank = higher tier. Used to label an in-place individual plan change
// as an upgrade vs a downgrade so a downgrade is never mislabeled "Upgrade".
const INDIVIDUAL_PLAN_RANK: Record<IndividualPlanCode, number> = {
  STARTER: 1,
  PROFESSIONAL: 2,
};

export type IndividualPlanCTA =
  | 'contact-support'
  | 'current-plan'
  | 'requires-team'
  | 'start-trial'
  | 'choose-plan'
  | 'switch-to-annual'
  | 'upgrade'
  | 'downgrade';

/**
 * Which call-to-action an individual (Starter/Professional) plan card should show, as a
 * translation-agnostic key -- PlanGrid maps this to `getTranslation(...)` so this stays a plain,
 * testable function (no raw strings baked in, per CLAUDE.md's getTranslation rules).
 *
 * `offerTrial` (driven by `hasEverSubscribed`) is what keeps 'start-trial' from being pitched to
 * a returning customer who canceled before -- EZ-4948.
 *
 * `seatsUsed` is the workspace's active member count. An individual plan whose bundled seat
 * allowance (see individualPlanSeats) is already smaller than that would put the customer over
 * seats the moment they bought it, and Team is the only plan that can actually cover them -- so
 * that card is 'requires-team' (a disabled, explanatory CTA) instead of a purchase, upgrade,
 * downgrade, or billing-cycle switch (EZ-5182).
 *
 * There is deliberately no seat-addition CTA here. Starter and Professional bundle a fixed seat
 * allowance (1 and 3 respectively) that isn't purchasable in units, so the card for a plan the
 * customer already holds is always terminal -- during a trial as much as after it (EZ-5172). A
 * subscriber who needs more seats moves to Team, which is what the over-seats banner points them
 * at (see SubscriptionTab's own note on omitting onAddSeats for individual plans).
 */
export function computeIndividualPlanCTA(params: {
  planCode: IndividualPlanCode;
  isCurrentPlan: boolean;
  hasTeamSubscription: boolean;
  currentIndividualPlan: IndividualPlanCode | null;
  offerTrial: boolean;
  seatsUsed: number;
}): IndividualPlanCTA {
  const {
    planCode,
    isCurrentPlan,
    hasTeamSubscription,
    currentIndividualPlan,
    offerTrial,
    seatsUsed,
  } = params;

  if (hasTeamSubscription) {
    return 'contact-support';
  }
  if (isCurrentPlan) {
    return 'current-plan';
  }
  // Quantity 1 is the plan's bundled allowance -- the only quantity a self-serve checkout or
  // in-place plan change ever buys.
  if (seatsUsed > individualPlanSeats(planCode, 1)) {
    return 'requires-team';
  }
  if (currentIndividualPlan === null) {
    return offerTrial ? 'start-trial' : 'choose-plan';
  }
  // Same tier, different cycle — only monthly → annual is reachable (annual →
  // monthly is support-gated), so it is always a switch to annual billing.
  if (planCode === currentIndividualPlan) {
    return 'switch-to-annual';
  }
  // Different tier — label by direction so a downgrade never reads "Upgrade".
  const isUpgrade = INDIVIDUAL_PLAN_RANK[planCode] > INDIVIDUAL_PLAN_RANK[currentIndividualPlan];
  return isUpgrade ? 'upgrade' : 'downgrade';
}
